/* eslint-disable tsdoc/syntax */
import { useCallback, useState, useEffect, useRef } from "react";
import {
  ApiResponseDTONINVerificationResponseDTO,
  NINVerificationResponseDTO,
  TierStepsResponseDTO,
  UsersApiService,
} from "@zenith/services/user";
import usePolling from "./usePolling";
import { updateTierStepStatus } from "@zenith/utils/Helpers";

const DEBUG = process.env.NODE_ENV === "development";
const log = (...args: any[]) =>
  DEBUG && console.log("[useNinVerificationPolling]", ...args);

/**
 * Hook for polling NIN verification status
 * @param ninValidationTrigger - Boolean flag to start polling
 * @param verifyingNin - Boolean indicating verification is in progress
 * @param upgradeContext - The upgrade context from context provider
 * @param setUpgradeContext - Function to update the upgrade context
 * @param enabled - Whether polling should be enabled
 */
export function useNinVerificationPolling(
  ninValidationTrigger: boolean,
  verifyingNin: boolean,
  upgradeContext: any,
  setUpgradeContext: (state: any) => void,
  enabled = true
) {
  const [verificationStatus, setVerificationStatus] = useState<
    string | undefined
  >(undefined);
  const [resultText, setResultText] = useState<string | undefined>(undefined);
  const [isPollingComplete, setIsPollingComplete] = useState(false);

  const upgradeContextRef = useRef(upgradeContext);
  upgradeContextRef.current = upgradeContext;

  const ninVerificationId = `nin-verification-${TierStepsResponseDTO.stepName.NIN_VERIFICATION_STEP}`;

  const fetchData = useCallback(
    () => UsersApiService.getNinVerificationStatus(),
    []
  );

  const { response, polling, error, reset } = usePolling(
    ninValidationTrigger && enabled && !isPollingComplete,
    !verifyingNin,
    fetchData,
    {
      pollingId: ninVerificationId,
      interval: 3000,
      maxPollingTime: 300000,
      maxRetries: 100,
      debug: DEBUG,
    }
  );

  useEffect(() => {
    if (!response) return;

    log("Received NIN verification response:", response);
    const data = (response as ApiResponseDTONINVerificationResponseDTO).data;
    const status = data?.verificationStatus;
    const resultMsg = data?.resultText;

    setVerificationStatus(status);
    setResultText(resultMsg);

    if (status) {
      const mappedStatus =
        status === NINVerificationResponseDTO.verificationStatus.SUCCESS
          ? TierStepsResponseDTO.stepStatus.APPROVED
          : status === NINVerificationResponseDTO.verificationStatus.FAILED
          ? TierStepsResponseDTO.stepStatus.REJECTED
          : status === NINVerificationResponseDTO.verificationStatus.PENDING
          ? TierStepsResponseDTO.stepStatus.PENDING
          : undefined;

      if (mappedStatus) {
        const ctx = upgradeContextRef.current;
        const tierStepsUpdated = updateTierStepStatus(
          ctx,
          TierStepsResponseDTO.stepName.NIN_VERIFICATION_STEP,
          mappedStatus
        );

        setUpgradeContext({
          ...ctx,
          tierSteps: tierStepsUpdated,
        });
      }
    }

    const isTerminalStatus =
      status !== NINVerificationResponseDTO.verificationStatus.PENDING;

    if (isTerminalStatus) {
      log(`NIN verification completed with status: ${status}`);
      setIsPollingComplete(true);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, reset, setUpgradeContext]);

  useEffect(() => {
    if (!ninValidationTrigger) {
      setIsPollingComplete(false);
      setVerificationStatus(undefined);
      setResultText(undefined);
    }
  }, [ninValidationTrigger]);

  useEffect(() => {
    log("Setting up NIN verification polling");
    return () => {
      log("Cleaning up NIN verification polling");
      reset();
    };
  }, [reset]);

  return {
    verificationStatus,
    polling: polling && !isPollingComplete,
    error,
    reset,
    resultText,
  };
}

export default useNinVerificationPolling;
